/**
 * 仕様グラフのノード型を web 側で扱うための語彙。
 *
 * 値の一次表現は共通部（`@cortex/domain-spec-graph`）の `SPEC_NODE_TYPES` で、contract の
 * `SpecNodeTypeSchema` がそれを写している。web が型の文字列を書き直すと語彙の追加が
 * 片側だけに入って 2 表現が乖離するため、contract の zod enum から実行時の配列と型の
 * 両方を導出する（core-principles の一次表現原則）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 仕様グラフのノード型を contract の zod enum から導出して web へ供給する。
 * @graph-connects none
 */
import { SpecNodeTypeSchema } from '@cortex/domain-spec-console';

/** 仕様グラフのノード型。表示順は contract の宣言順に従う。 @graph-connects none */
export const SPEC_NODE_TYPE_VALUES = SpecNodeTypeSchema.options;

/** 仕様グラフのノード型。 */
export type SpecNodeTypeValue = (typeof SPEC_NODE_TYPE_VALUES)[number];

/**
 * ノード型ごとの表示名の翻訳キー。
 *
 * 型バッジと、型を列として並べる一覧の両方がこれを引く。片方が自前の対応表を持つと、
 * 同じ型が画面によって違う呼び名で出る。
 *
 * @param nodeType 仕様グラフのノード型
 * @returns 表示名の翻訳キー
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ノード型から表示名の翻訳キーを引く。
 * @graph-connects none
 */
export function nodeTypeLabelKey(nodeType: SpecNodeTypeValue): string {
  return `spec.nodeType${LABEL_KEY_SUFFIX[nodeType]}`;
}

/**
 * 表示名の翻訳キーの末尾。業務フローはストーリーの文言で出す — 画面を作り直すまで、API が
 * この型をストーリーとして見せていたのと同じ表示を保つ（文言は画面を作り直す F2 が足す）。
 * @graph-connects none
 */
const LABEL_KEY_SUFFIX: Readonly<Record<SpecNodeTypeValue, string>> = {
  Stage: 'Stage',
  Flow: 'UserStory',
  Business: 'Business',
  Rule: 'Rule',
  Term: 'Term',
  Verb: 'Verb',
  Unit: 'Unit',
  Actor: 'Actor',
  Domain: 'Domain',
  Screen: 'Screen',
  Component: 'Component',
};
